import { useState } from "react";
import { useTranslation } from "react-i18next";
import { withdrawMyRequest } from "../../api/providerAllocation";
import { useToast } from "../toast/toastContext";
import styles from "./CancelRequestButton.module.css";

/**
 * Lets a person cancel a request of their own that is still waiting. The request is withdrawn, which takes it out of its approver's
 * queue; the outcome is a toast. It shows nothing for a request that is no longer pending.
 *
 * @param props.requestId - The request.
 * @param props.status - The request's status; only a pending request can be cancelled.
 * @param props.onCancelled - Called after the request is withdrawn, so the page can reload its requests.
 */
export function CancelRequestButton({
  requestId,
  status,
  onCancelled,
}: {
  requestId: string;
  status: string;
  onCancelled: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (status !== "Pending") {
    return null;
  }

  const cancel = async (): Promise<void> => {
    setBusy(true);
    const result = await withdrawMyRequest(requestId);
    setBusy(false);
    if (result.kind === "ok") {
      toast.show(t("myRequests.cancelled"), "success");
      onCancelled();
    } else {
      toast.show(
        result.kind === "denied"
          ? t("myRequests.cancelDenied")
          : result.message,
        "error",
      );
    }
  };

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={`button button-secondary ${styles.small}`}
        disabled={busy}
        onClick={() => void cancel()}
      >
        {t("myRequests.cancel")}
      </button>
    </div>
  );
}
